import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { getIntegrationByType } from '@/components/organisms/Integrations';
import { FilterEmptyState } from '@/components/ui/custom/FilterEmptyState';
import { selectAvailableChannels } from '@/redux/integrations/selectors';
import { IntegrationType } from '@/shared/types/integration';
import { getChannelKey } from '@/util/channelName';

import { FilterCheckboxItem } from './filters/FilterCheckboxItem';
import { SearchableCheckboxList } from './filters/SearchableCheckboxList';
import { FilterPanelProps } from './filters/types';

/**
 * Renders a list of available channels with checkboxes for filtering
 */
export const ChannelsFilter = ({
  filterIndex,
  searchText = '',
  onSearchChange = () => {},
}: FilterPanelProps) => {
  const { t } = useTranslation();
  const availableChannels = useSelector(selectAvailableChannels);
  const { control, getValues } = useFormContext();

  const { update } = useFieldArray({ control, name: 'filters' });

  const channels = useWatch({ control, name: `filters.${filterIndex}.channels` }) || [];

  const options = availableChannels.map((channel: IntegrationType) => ({
    channel,
    label: t(getChannelKey(channel)),
  }));

  const handleChannelToggle = (channel: string, checked: boolean) => {
    const currentChannels = Array.isArray(channels) ? [...channels] : [];
    const newChannels = checked
      ? [...currentChannels, channel]
      : currentChannels.filter((c: string) => c !== channel);

    const currentFilter = getValues(`filters.${filterIndex}`) as { type?: string } | undefined;
    const fallbackType = currentFilter?.type ?? 'channels';
    const safeFilter =
      currentFilter && typeof currentFilter === 'object' ? currentFilter : { type: fallbackType };
    const nextFilter = {
      ...safeFilter,
      type: safeFilter.type ?? fallbackType,
      channels: newChannels,
    };

    update(filterIndex, nextFilter);
  };

  if (options.length === 0) {
    return <FilterEmptyState text={t('analytics.filters.no_channels')} />;
  }

  return (
    <SearchableCheckboxList
      searchText={searchText}
      onSearchChange={onSearchChange}
      items={options}
      getSearchTerms={({ label }) => [label]}
      placeholder={t('analytics.filters.search_channels')}
      renderItem={({ channel, label }) => {
        const Icon = getIntegrationByType(channel.toLowerCase() as IntegrationType)?.Icon;

        return (
          <FilterCheckboxItem
            key={channel}
            id={`channel-${channel}`}
            checked={channels.includes(channel)}
            onCheckedChange={(checked) => handleChannelToggle(channel, checked as boolean)}
          >
            <div className="flex items-center gap-4">
              {Icon ? <Icon size={16} /> : null}
              <span>{label}</span>
            </div>
          </FilterCheckboxItem>
        );
      }}
    />
  );
};
